export const GUIDES = [
  {
    slug: "email-marketing-101",
    overline: "Guide · Fundamentals",
    title: "Email Marketing 101: A Beginner's Guide to Building Your First Campaign",
    readTime: "11 min read",
    lede: "Everything you need to send your first real campaign this week — picking a platform, building a list legally, designing the email, and reading the results without drowning in jargon.",
    featured: ["mailerlite", "getresponse"],
    sections: [
      {
        h: "Why email still beats every other channel",
        body: [
          "Social algorithms decide who sees your posts; email arrives directly in an inbox your subscriber checks daily. The economics explain why businesses keep investing: the widely cited industry benchmark is a $36-$42 return for every $1 spent, and unlike ad platforms, your list is an asset you own. Nobody can raise your rent.",
          "The catch is that email only works when people asked to hear from you. Everything in this guide assumes a permission-based list — bought lists destroy deliverability and, in many jurisdictions, break the law.",
        ],
      },
      {
        h: "Step 1 — Pick your platform",
        body: [
          "Your platform choice matters less than starting, but switching later is work, so choose deliberately. For most beginners we recommend MailerLite (best value, friendliest editor, free to 1,000 subscribers) or GetResponse (best long-term feature ceiling, free to 500 contacts). Creators and newsletter writers should look at ConvertKit's 10,000-subscriber free plan first.",
          "Evaluate three things only at this stage: the free or entry price at your expected list size, whether automation is included at that tier, and whether the editor feels good to you in a 30-minute trial. Everything else is optimization.",
        ],
      },
      {
        h: "Step 2 — Build your list the legal way",
        body: [
          "Add a signup form to your website's highest-traffic pages: homepage, blog sidebar, and checkout or contact confirmation. Offer a reason to subscribe — a checklist, discount, or template converts 3-5x better than 'subscribe to our newsletter.'",
          "Two legal musts: only email people who opted in (GDPR, CAN-SPAM, and CASL all require it), and include a working unsubscribe link in every send — your platform adds this automatically; never remove it.",
        ],
        list: ["Create one lead magnet your audience actually wants", "Place forms on your 3 highest-traffic pages", "Turn on double opt-in while your list is young", "Import existing contacts only with documented consent"],
      },
      {
        h: "Step 3 — Write and design your first campaign",
        body: [
          "Structure every email around one idea and one call to action. Subject line: 30-50 characters, specific over clever ('Your March invoice checklist' beats 'Don't miss this!'). Preview text: extend the subject, don't repeat it. Body: short paragraphs, one button, mobile-first — over 60% of opens happen on phones.",
          "Resist the urge to over-design. Single-column layouts with one image and generous whitespace consistently outperform busy multi-column newsletters for small businesses.",
        ],
      },
      {
        h: "Step 4 — Send, then read the right numbers",
        body: [
          "Send your first campaign to your full (small) list mid-week morning, then watch three numbers: open rate (25%+ is healthy for a new permission-based list), click rate (2-4% is typical), and unsubscribes (under 0.3% per campaign is fine — pruning is natural).",
          "Ignore open-rate precision: Apple Mail Privacy Protection inflates opens since 2021. Clicks and conversions are your truth metrics. Your first campaign's job is a baseline, not a home run.",
        ],
      },
      {
        h: "Your first 90 days",
        body: [
          "Week 1: platform, form, lead magnet. Week 2: first campaign. Weeks 3-4: a 3-email welcome automation — this alone will out-earn your broadcasts. Month 2: settle into a consistent cadence you can sustain (weekly or biweekly). Month 3: start A/B testing subject lines and prune your first inactives.",
        ],
      },
    ],
  },
  {
    slug: "automation-workflows",
    overline: "Guide · Automation",
    title: "Email Automation Workflows That Convert: Real Case Studies",
    readTime: "13 min read",
    lede: "Automated emails generate roughly 30x more revenue per recipient than one-off campaigns, because they arrive at the moment of intent. These are the five workflows worth building first — with the numbers to prove it.",
    featured: ["getresponse", "moosend"],
    sections: [
      {
        h: "Why automation outperforms broadcasts",
        body: [
          "A broadcast email competes with everything else in the inbox at an arbitrary time. An automated email arrives in response to a behavior — a signup, a purchase, an abandoned cart — when attention is at its peak. Industry data consistently shows automated emails earning 2-4x the open rates and 5-10x the click rates of campaigns.",
          "The strategic shift: stop thinking of email as a megaphone and start treating it as a concierge. Every workflow below answers a question the subscriber is already asking.",
        ],
      },
      {
        h: "Workflow 1 — The welcome series (case: freelance designer)",
        body: [
          "Trigger: new subscriber. Structure: 4-5 emails over 10 days — deliver the lead magnet, tell your story, teach one quick win, handle the biggest objection, make one clear offer. This is the highest-leverage automation that exists; it sets the tone for the entire relationship.",
          "Case study: a freelance brand designer we advised replaced a single 'thanks for subscribing' email with a 5-email series pitching a $350 brand audit. Result over 6 months: 19 audits booked directly from the sequence — $6,650 in revenue from a workflow that took one afternoon to write.",
        ],
      },
      {
        h: "Workflow 2 — Abandoned cart recovery (case: homeware store)",
        body: [
          "Trigger: checkout started, no purchase. Structure: reminder at 1 hour, objection-handling at 24 hours, small incentive at 72 hours. Keep the first email helpful, not salesy — 'Still thinking it over?' outperforms 'You forgot something!'",
          "Case study: a Shopify homeware store on Moosend recovered 8.2% of abandoned carts with this exact timing, adding ~$1,900/month on a $32/month plan. The third email's 10% discount was redeemed by only 31% of recoverers — most people just needed the reminder.",
        ],
      },
      {
        h: "Workflow 3 — Post-purchase & cross-sell",
        body: [
          "Trigger: order placed. Structure: delivery/usage tips (day 3), review request (day 10), complementary product (day 21). The review request alone compounds: more reviews lift conversion, which makes every future email more profitable.",
          "The mistake to avoid: pitching immediately. Lead with helping them succeed with what they bought. The cross-sell converts twice as well when it follows genuine utility.",
        ],
      },
      {
        h: "Workflow 4 — Re-engagement (win-back)",
        body: [
          "Trigger: no opens in 60-90 days. Structure: 'Still interested?' with a compelling reason to stay, a second email offering a preference downgrade (weekly → monthly), and a final 'we'll remove you' notice. Typically 5-12% of inactives re-engage — and the rest get pruned, which improves your deliverability for everyone else.",
        ],
      },
      {
        h: "Workflow 5 — Lead nurture for high-ticket services",
        body: [
          "Trigger: downloaded a resource or requested a quote. Structure: 5-7 emails over 3 weeks mixing proof (case studies, numbers), process transparency, and soft calls to book a call. High-ticket buyers need 7+ touchpoints; automation delivers them without you hovering over a spreadsheet.",
          "Case study: a 3-person agency using GetResponse scoring (points per email click + pricing page visit) only triggered the sales email when a lead crossed 50 points. Consultation bookings rose 40% because pitches finally arrived when prospects were warm.",
        ],
      },
      {
        h: "Building order: what to do first",
        body: [
          "Build in this order: welcome series → abandoned cart (if ecommerce) or nurture (if services) → post-purchase → win-back. Each takes an afternoon inside a visual builder like GetResponse's or Moosend's. Resist building all five at once; a live imperfect workflow beats four perfect drafts.",
        ],
      },
    ],
  },
  {
    slug: "segmentation-strategies",
    overline: "Guide · Segmentation",
    title: "Email Segmentation Strategies to Increase Open Rates",
    readTime: "10 min read",
    lede: "Segmented campaigns earn roughly 2x the clicks of batch-and-blast sends. You don't need a data team — you need four segments, built once, that update themselves forever.",
    featured: ["mailerlite", "moosend"],
    sections: [
      {
        h: "The problem with one big list",
        body: [
          "When everyone gets everything, most emails are irrelevant to most people — and irrelevance trains subscribers to ignore you. Gmail notices the ignoring and starts routing you to Promotions, then spam. Segmentation is therefore not a growth hack; it is deliverability insurance.",
          "The good news: every platform we review supports dynamic segments that update automatically. You build the rule once; the segment maintains itself.",
        ],
      },
      {
        h: "Segment 1 — Signup source",
        body: [
          "Where someone joined tells you what they want. A subscriber from your 'pricing guide' lead magnet has buying intent; one from a giveaway has curiosity. Tag every form and import with its source, then tailor the welcome series and offer accordingly. This single segment typically lifts click rates 30-50% on its own.",
        ],
      },
      {
        h: "Segment 2 — Engagement level",
        body: [
          "Split your list into engaged (opened or clicked in 30 days), cooling (31-90 days), and cold (90+). Send your best content to engaged subscribers most frequently; send cooling subscribers your proven greatest hits; send cold subscribers only win-back campaigns. This protects sender reputation while focusing effort where returns are.",
        ],
      },
      {
        h: "Segment 3 — Customer vs. prospect",
        body: [
          "Nothing erodes trust like being pitched a product you already own. Tag purchases via your store integration (Shopify, WooCommerce, Stripe) and always exclude buyers from acquisition campaigns. Then flip it: customers-only emails — tips, loyalty offers, early access — are your highest-revenue sends.",
        ],
      },
      {
        h: "Segment 4 — Interest or product category",
        body: [
          "Let subscribers self-select. Checkboxes on your signup form ('What are you interested in?') and link-click tagging ('clicked: running shoes → tag: running') build interest profiles passively. A bookstore tagging fiction vs. business readers can double relevant-click rates by simply not sending romance promos to finance readers.",
        ],
      },
      {
        h: "How far is too far?",
        body: [
          "Past 6-8 segments, maintenance costs exceed returns for small teams. Hyper-segmentation is an enterprise game played with dedicated staff and CDPs. Four well-maintained dynamic segments will capture 90% of the available lift — spend your remaining time writing better emails instead.",
        ],
      },
    ],
  },
  {
    slug: "ab-testing",
    overline: "Guide · Testing",
    title: "A/B Testing Email Campaigns: The Complete Guide",
    readTime: "12 min read",
    lede: "A/B testing turns opinions into evidence. Done right, it compounds: each test permanently raises the baseline every future email performs against. Here's how to run tests that actually reach significance.",
    featured: ["getresponse", "mailerlite"],
    sections: [
      {
        h: "What A/B testing actually is (and isn't)",
        body: [
          "An email A/B test sends two variants to small random slices of your list, measures a chosen metric, then sends the winner to the remainder. It is not guessing with extra steps — randomization and a single changed variable are what make the result trustworthy.",
          "Change one thing per test. If variant B has a new subject line and a new CTA button, you'll know it won but never why — and 'why' is the asset you're building.",
        ],
      },
      {
        h: "What to test, in order of impact",
        body: [
          "Subject lines first: they gate every other metric. Then send time/day, from-name (a person's name vs. brand), primary CTA copy and placement, content length, and finally design elements. Button-color tests are real but their effect sizes are usually small compared to subject lines.",
        ],
        list: ["Subject line: specificity vs. curiosity", "From name: 'Maya at Acme' vs. 'Acme'", "CTA: benefit-led ('Get my checklist') vs. generic ('Download')", "Length: 80-word punchy vs. 300-word story", "Send time: Tuesday 9am vs. Thursday 2pm"],
      },
      {
        h: "Sample size and statistical significance",
        body: [
          "The uncomfortable math: to detect a 20% relative lift in click rate with confidence, you need roughly 1,000+ recipients per variant. On a 2,000-subscriber list, testing a 20/20 split is workable; on a 400-subscriber list, almost nothing reaches significance.",
          "Small-list alternative: test across time instead. Run subject-line style A for four consecutive campaigns, then style B for four, comparing average performance. Slower, but honest.",
        ],
      },
      {
        h: "How to run the test",
        body: [
          "In GetResponse or MailerLite: create the campaign, enable A/B testing, choose your variable, set the test split (20/20 to 30/30 is standard) and the winner metric. Set the test window to at least 4 hours — longer for lists with weekend or international skew — before the winner goes to the remaining 60-80%.",
          "One modern caveat: with Apple MPP inflating opens, prefer click rate as your winner metric whenever the email has a link. Opens can crown a false winner.",
        ],
      },
      {
        h: "Building a testing habit",
        body: [
          "Keep a simple log: date, variable, variants, result, takeaway. After 90 days of weekly tests you'll have a proprietary playbook — your audience's proven preferences — that no competitor can copy. That compounding knowledge is the real ROI of testing, not any single uplift.",
        ],
      },
    ],
  },
  {
    slug: "build-email-list",
    overline: "Guide · List Building",
    title: "How to Build Your Email List from Scratch: 10 Proven Tactics",
    readTime: "12 min read",
    lede: "Zero subscribers is where every great list started. These ten tactics are ordered by effort-to-return for small businesses — no bought lists, no gimmicks, just compounding owned audience.",
    featured: ["convertkit", "mailerlite"],
    sections: [
      {
        h: "The foundation: a lead magnet worth trading for",
        body: [
          "An email address is currency; people spend it on value. The highest-converting lead magnets solve one specific problem fast: checklists, templates, calculators, swipe files, and short email courses. 'Subscribe for updates' converts under 1%; a targeted template converts 5-15% on the same traffic.",
        ],
      },
      {
        h: "Tactics 1-5: your own properties",
        body: [
          "Start where you already have attention. Every tactic below is free and live within a week.",
        ],
        list: [
          "1. Exit-intent popup offering your lead magnet (converts 2-4% of abandoning visitors)",
          "2. Inline forms inside your 5 most-read blog posts, matched to each post's topic",
          "3. Email signature link — everyone on your team, every email, every day",
          "4. Checkout or booking-confirmation opt-in (your warmest possible audience)",
          "5. A dedicated landing page for the lead magnet, linked from every social bio",
        ],
      },
      {
        h: "Tactics 6-8: borrowed audiences",
        body: [
          "Borrowed audiences grow lists faster than owned ones early on. ConvertKit's Creator Network institutionalizes this — creators recommend each other's newsletters and both grow. You can replicate it manually with any peer business serving the same audience non-competitively.",
        ],
        list: [
          "6. Newsletter cross-promotions with 2-3 peer businesses",
          "7. Guest posts and podcast appearances with a landing-page CTA (not a homepage link)",
          "8. Co-hosted webinars where registration feeds both lists with consent",
        ],
      },
      {
        h: "Tactics 9-10: accelerants",
        body: [
          "9. Referral program: give subscribers a reason to share — a milestone reward ('refer 3 friends, get the premium template') turns readers into recruiters. Kit's Creator Pro has this built in; elsewhere, a simple tagged link works.",
          "10. Paid lead ads: once a subscriber is provably worth $X to you, Facebook/Instagram lead ads buying subscribers at less than $X become a growth tap you control. Only turn this on after your welcome series proves its conversion rate.",
        ],
      },
      {
        h: "What to expect, honestly",
        body: [
          "A realistic trajectory for a new small business doing the above consistently: 100-300 subscribers in month one, 1,000 by month four or five. Growth is exponential because each tactic feeds the others — the referral program needs a list, the cross-promos need credibility. Slow is not failure; it is the shape of the curve.",
        ],
      },
    ],
  },
  {
    slug: "deliverability",
    overline: "Guide · Deliverability",
    title: "Email Deliverability: Why Your Emails Aren't Reaching the Inbox",
    readTime: "13 min read",
    lede: "Roughly 1 in 6 legitimate marketing emails never reaches the inbox. If your open rates slid recently, the problem is probably not your subject lines — it's your sender reputation. Here's how to diagnose and fix it.",
    featured: ["getresponse", "aweber"],
    sections: [
      {
        h: "How inbox placement actually works",
        body: [
          "Gmail, Outlook, and Yahoo score every sender on engagement history, authentication, and complaint rates — then route accordingly. Your email platform's reputation helps, but since Gmail's 2024 sender requirements, your own domain reputation is the dominant factor. You are the sender of record, not MailerLite or GetResponse.",
        ],
      },
      {
        h: "Fix 1 — Authenticate: SPF, DKIM, DMARC",
        body: [
          "These three DNS records prove your emails are really from you. Since February 2024, Google and Yahoo effectively require all three for bulk senders — unauthenticated mail is increasingly rejected outright, not just filtered. Every platform in our reviews provides the exact records to paste into your DNS; it's a 20-minute job with permanent upside.",
          "Check your status free with a mail-tester tool or your platform's built-in domain checker. If DMARC shows 'none' or missing, stop reading and fix it now — nothing else on this page matters until that's done.",
        ],
      },
      {
        h: "Fix 2 — Prune relentlessly",
        body: [
          "Every send to a chronically inactive subscriber hurts you: low engagement teaches Gmail your mail is ignorable. Run a quarterly win-back; remove or suppress anyone who doesn't respond. A 5,000-subscriber list with 60% engagement out-delivers a 15,000 list with 20% engagement — and reaches more actual humans.",
        ],
      },
      {
        h: "Fix 3 — Send like a human, not a cannon",
        body: [
          "Sudden volume spikes trigger filters: if you normally send weekly to 2,000 and suddenly blast 20,000 imported contacts, expect throttling. Warm up volume gradually (double at most week over week), keep a consistent cadence, and never send to addresses older than your last engagement confirmation.",
        ],
      },
      {
        h: "Fix 4 — Watch the metrics that matter",
        body: [
          "Spam complaint rate is the killer metric: Gmail requires it under 0.3%, and you should treat 0.1% as your real ceiling. Monitor it in Google Postmaster Tools (free) alongside your domain reputation. Also watch bounce rate (keep under 2%) — high bounces signal stale data and erode trust fast.",
        ],
      },
      {
        h: "Fix 5 — Content hygiene",
        body: [
          "Content matters less than reputation, but it still matters: balance image-to-text ratio, avoid URL shorteners, skip spam-trigger formatting (ALL CAPS subject lines, excessive exclamation points), and always include a plain-text version. None of these rescue a bad reputation; all of them polish a good one.",
        ],
      },
      {
        h: "When to get a dedicated IP",
        body: [
          "Shared IPs (the default) are fine — usually better — below ~50,000 emails per month, because your platform's pool carries established reputation. Above that volume, a dedicated IP (available on GetResponse MAX, Moosend+, AWeber Unlimited) gives you full control over your own reputation — with full responsibility for warming it up.",
        ],
      },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
